<template>
  <span
    :class="[
      'inline-flex items-center font-medium',
      sizeClasses,
      variantClasses,
      pill ? 'rounded-full' : 'rounded',
      removable ? 'pr-1' : ''
    ]"
  >
    <!-- Icon Slot -->
    <span v-if="$slots.icon" :class="iconSizeClasses">
      <slot name="icon" />
    </span>

    <!-- Default Content -->
    <slot />

    <!-- Remove Button -->
    <button
      v-if="removable"
      type="button"
      @click="handleRemove"
      :class="[
        'flex-shrink-0 ml-0.5 inline-flex items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2',
        removeButtonClasses
      ]"
    >
      <span class="sr-only">Remove</span>
      <svg :class="removeIconSizeClasses" fill="currentColor" viewBox="0 0 20 20">
        <path
          fill-rule="evenodd"
          d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
          clip-rule="evenodd"
        />
      </svg>
    </button>
  </span>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  variant: {
    type: String,
    default: 'secondary',
    validator: (value) => [
      'primary',
      'secondary',
      'success',
      'warning',
      'danger',
      'info',
      'light',
    ].includes(value),
  },
  size: {
    type: String,
    default: 'md',
    validator: (value) => ['xs', 'sm', 'md', 'lg'].includes(value),
  },
  pill: {
    type: Boolean,
    default: true,
  },
  removable: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['remove']);

/**
 * Get size-related classes
 */
const sizeClasses = computed(() => {
  const sizes = {
    xs: 'px-2 py-0.5 text-xs',
    sm: 'px-2.5 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-1.5 text-base',
  };
  return sizes[props.size];
});

/**
 * Get icon size classes
 */
const iconSizeClasses = computed(() => {
  const sizes = {
    xs: 'mr-1 -ml-0.5',
    sm: 'mr-1 -ml-0.5',
    md: 'mr-1.5 -ml-0.5',
    lg: 'mr-2 -ml-1',
  };
  return sizes[props.size];
});

/**
 * Get remove icon size classes
 */
const removeIconSizeClasses = computed(() => {
  const sizes = {
    xs: 'h-3 w-3',
    sm: 'h-3.5 w-3.5',
    md: 'h-4 w-4',
    lg: 'h-5 w-5',
  };
  return sizes[props.size];
});

/**
 * Get variant-specific classes
 */
const variantClasses = computed(() => {
  const variants = {
    primary: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
    secondary: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
    success: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
    warning: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
    danger: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
    info: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300',
    light: 'bg-white text-gray-700 border border-gray-300 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600',
  };
  return variants[props.variant];
});

/**
 * Get remove button classes based on variant
 */
const removeButtonClasses = computed(() => {
  const variants = {
    primary: 'text-blue-600 hover:bg-blue-200 dark:text-blue-400 dark:hover:bg-blue-800/50',
    secondary: 'text-gray-600 hover:bg-gray-200 dark:text-gray-400 dark:hover:bg-gray-600',
    success: 'text-green-600 hover:bg-green-200 dark:text-green-400 dark:hover:bg-green-800/50',
    warning: 'text-yellow-600 hover:bg-yellow-200 dark:text-yellow-400 dark:hover:bg-yellow-800/50',
    danger: 'text-red-600 hover:bg-red-200 dark:text-red-400 dark:hover:bg-red-800/50',
    info: 'text-cyan-600 hover:bg-cyan-200 dark:text-cyan-400 dark:hover:bg-cyan-800/50',
    light: 'text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700',
  };
  return variants[props.variant];
});

/**
 * Handle remove button click
 */
const handleRemove = (event) => {
  emit('remove', event);
};
</script>
